import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@material-ui/core";
import { Confirm, useNotify } from "react-admin";
import { cloDelete, cloError, cloGet, cloPatch, cloPost, ENDPOINTS, formatDate, MATCH_TYPES } from "./api";
import CandidateSearch, { Candidate } from "./CandidateSearch";
import MerchantPicker from "./MerchantPicker";
import PatternDialog from "./PatternDialog";

interface Props {
  cloMerchantId: number | null;
  categories: any[];
  onClose: () => void;
  onChanged: () => void;
}

const MerchantDetail: React.FC<Props> = ({ cloMerchantId, categories, onClose, onChanged }) => {
  const notify = useNotify();
  const [merchant, setMerchant] = useState<any | null>(null);
  const [form, setForm] = useState({ name: "", categoryId: "", status: "active" });
  const [patterns, setPatterns] = useState<any[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [editingPattern, setEditingPattern] = useState<any | null | undefined>(undefined);
  const [addingSource, setAddingSource] = useState(false);
  const [mergeInto, setMergeInto] = useState("");
  const [confirmingMerge, setConfirmingMerge] = useState(false);

  const load = useCallback(async () => {
    if (!cloMerchantId) return;
    try {
      const [detail, list] = await Promise.all([
        cloGet(`${ENDPOINTS.CLO_MERCHANTS}/${cloMerchantId}`),
        cloGet(ENDPOINTS.CLO_MERCHANT_PATTERNS, { cloMerchantId, includeRetired: showRetired }),
      ]);
      setMerchant(detail.data);
      setForm({ name: detail.data.name, categoryId: detail.data.category_id ? String(detail.data.category_id) : "", status: detail.data.status });
      setPatterns(list.data || []);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [cloMerchantId, showRetired, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<any>, message: string) => {
    try {
      await fn();
      notify(message, "info");
      onChanged();
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const saveDetails = () =>
    run(
      () => cloPatch(`${ENDPOINTS.CLO_MERCHANTS}/${cloMerchantId}`, {
        name: form.name,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        ...(merchant.status !== "merged" ? { status: form.status } : {}),
      }),
      "Merchant saved"
    );

  const addSource = (c: Candidate) =>
    run(() => cloPost(ENDPOINTS.CLO_MERCHANT_ACTIONS, { action: "add_source", cloMerchantId, brandId: c.id }), `${c.name} added`);

  const removeSource = (sourceRowId: number) =>
    run(() => cloPost(ENDPOINTS.CLO_MERCHANT_ACTIONS, { action: "remove_source", cloMerchantId, sourceRowId }), "Source removed");

  const retirePattern = (id: number) => run(() => cloDelete(`${ENDPOINTS.CLO_MERCHANT_PATTERNS}/${id}`), "Pattern retired");

  const merge = async () => {
    setConfirmingMerge(false);
    try {
      await cloPost(ENDPOINTS.CLO_MERCHANT_ACTIONS, { action: "merge", cloMerchantId, intoCloMerchantId: Number(mergeInto) });
      notify("Merchants merged", "info");
      onChanged();
      onClose();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const active = merchant?.status === "active";

  return (
    <Dialog disableBackdropClick open={cloMerchantId !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {merchant?.name || "Merchant"}
        {merchant?.partner_name && <Chip size="small" color="primary" label={`CLO retailer · ${merchant.partner_name}`} style={{ marginLeft: 8 }} />}
        {merchant && merchant.status !== "active" && <Chip size="small" label={merchant.status} style={{ marginLeft: 8 }} />}
      </DialogTitle>
      {merchant && (
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth margin="dense" label="Name" value={form.name} disabled={merchant.status === "merged"} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth margin="dense" label="Category" value={form.categoryId} disabled={merchant.status === "merged"} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.display_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth margin="dense" label="Status" value={form.status} disabled={merchant.status === "merged"} onChange={(e) => setForm({ ...form, status: e.target.value })}
                helperText={merchant.brand_id ? "A CLO retailer's own merchant can't be disabled: its customers' history decides their cohorts." : undefined}>
                <MenuItem value="active">active</MenuItem>
                <MenuItem value="disabled" disabled={Boolean(merchant.brand_id) && merchant.status !== "disabled"}>disabled</MenuItem>
                {merchant.status === "merged" && <MenuItem value="merged">merged</MenuItem>}
              </TextField>
            </Grid>
          </Grid>
          {merchant.status !== "merged" && (
            <Box textAlign="right">
              <Button size="small" color="primary" onClick={saveDetails}>Save details</Button>
            </Box>
          )}

          <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Sources</Typography>
            {active && <Button size="small" color="primary" onClick={() => setAddingSource(!addingSource)}>{addingSource ? "Done" : "Add source"}</Button>}
          </Box>
          <Table size="small">
            <TableBody>
              {merchant.sources.map((s: any) => (
                <TableRow key={s.id}>
                  <TableCell>{s.brand_name}</TableCell>
                  <TableCell>{s.provider_name}</TableCell>
                  <TableCell align="right">{active && <Button size="small" onClick={() => removeSource(s.id)}>Remove</Button>}</TableCell>
                </TableRow>
              ))}
              {merchant.sources.length === 0 && <TableRow><TableCell colSpan={3}>No sources; matched by bank text only</TableCell></TableRow>}
            </TableBody>
          </Table>
          {addingSource && (
            <CandidateSearch
              picked={merchant.sources.map((s: any) => s.brand_id)}
              onPick={addSource}
            />
          )}

          <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Bank text patterns</Typography>
            <Box>
              <FormControlLabel control={<Switch size="small" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />} label="Show retired" />
              {active && <Button size="small" color="primary" onClick={() => setEditingPattern(null)}>Add pattern</Button>}
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pattern</TableCell>
                <TableCell>Match</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Changed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {patterns.map((p) => (
                <TableRow key={p.id} style={p.is_active ? undefined : { opacity: 0.5 }}>
                  <TableCell><code>{p.pattern}</code></TableCell>
                  <TableCell>{MATCH_TYPES[p.match_type] || p.match_type}</TableCell>
                  <TableCell>{p.channel || "—"}</TableCell>
                  <TableCell>{p.version}</TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">{p.is_active ? `Added by ${p.created_by}` : `Retired by ${p.retired_by}`}</Typography>
                    <Typography variant="caption" display="block">{formatDate(p.is_active ? p.createdAt : p.retired_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {p.is_active && active && (
                      <>
                        <Button size="small" onClick={() => setEditingPattern(p)}>Edit</Button>
                        <Button size="small" color="secondary" onClick={() => retirePattern(p.id)}>Retire</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {patterns.length === 0 && <TableRow><TableCell colSpan={6} align="center">No patterns yet</TableCell></TableRow>}
            </TableBody>
          </Table>

          <Box mt={3}>
            <Typography variant="subtitle1">Groups</Typography>
            {merchant.groups.length ? merchant.groups.map((g: any) => <Chip key={g.id} size="small" label={g.name} style={{ margin: 2 }} />) : <Typography variant="body2" color="textSecondary">Not in any group</Typography>}
          </Box>

          {active && (
            <Box mt={3}>
              <Typography variant="subtitle1">Merge into another merchant</Typography>
              <Typography variant="body2" color="textSecondary">Use when the same shop was added twice. This record is kept as merged.</Typography>
              <MerchantPicker label="Merge into" value={mergeInto} onChange={setMergeInto} excludeId={merchant.id} />
              <Box textAlign="right">
                <Button size="small" color="secondary" disabled={!mergeInto} onClick={() => setConfirmingMerge(true)}>Merge</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {merchant && (
        <PatternDialog
          open={editingPattern !== undefined}
          cloMerchantId={merchant.id}
          pattern={editingPattern ?? null}
          onClose={() => setEditingPattern(undefined)}
          onSaved={() => { setEditingPattern(undefined); onChanged(); load(); }}
        />
      )}
      <Confirm
        isOpen={confirmingMerge}
        title={`Merge ${merchant?.name}?`}
        content="It moves into the selected merchant, with its sources, patterns and groups."
        confirm="Merge"
        onConfirm={merge}
        onClose={() => setConfirmingMerge(false)}
      />
    </Dialog>
  );
};

export default MerchantDetail;
